import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PayoutRequest {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: "pending" | "approved" | "paid" | "rejected" | "cancelled" | string;
  bank_name: string | null;
  account_number: string | null;
  account_holder: string | null;
  branch_code: string | null;
  account_type: string | null;
  note: string | null;
  admin_note: string | null;
  transfer_reference: string | null;
  processed_at: string | null;
  created_at: string;
}

const table = () => (supabase as unknown as { from: (t: string) => any }).from("payout_requests");

export const useMyPayouts = (userId?: string | null) =>
  useQuery({
    queryKey: ["payout-requests", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await table().select("*").eq("user_id", userId).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PayoutRequest[];
    },
  });

export const useAllPayouts = () =>
  useQuery({
    queryKey: ["payout-requests", "all"],
    queryFn: async () => {
      const { data, error } = await table().select("*").order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as PayoutRequest[];
    },
  });

export interface NewPayoutRequest {
  user_id: string;
  amount: number;
  bank_name?: string | null;
  account_number?: string | null;
  account_holder?: string | null;
  branch_code?: string | null;
  account_type?: string | null;
  note?: string | null;
}

export const useRequestPayout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NewPayoutRequest) => {
      const { data, error } = await table().insert(payload).select().single();
      if (error) throw error;
      return data as PayoutRequest;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payout-requests"] }),
  });
};

export const useUpdatePayout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<PayoutRequest> & { id: string }) => {
      const { error } = await table().update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payout-requests"] }),
  });
};

/** Sends an approved payout to the creator's bank via the Paystack transfer gateway. */
export const useSendPayoutToBank = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payoutId: string) => {
      const { data, error } = await supabase.functions.invoke("paystack-payout", {
        body: { payout_id: payoutId },
      });
      if (error) throw error;
      const payload = data as { ok?: boolean; status?: string; reference?: string; error?: string };
      if (payload?.error) throw new Error(payload.error);
      return payload;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payout-requests"] }),
  });
};
